/**
 * Prepares CMS rich text HTML for rendering on the fixed content pages (DLK-911).
 *
 * The API already sanitizes the HTML server-side (DLK-910), so nothing here
 * strips tags. These transforms only add attributes/wrappers that the CMS editor
 * cannot author but the acceptance criteria require:
 *  - images get native lazy loading
 *  - tables get a horizontally scrollable wrapper so they never overflow on mobile
 *  - external links open in a new tab without leaking the referring window
 *
 * Runs on the server so the finished markup ships in the initial SSR payload.
 */

/** Class name the rich text styles hook onto to scroll wide tables. */
export const TABLE_SCROLL_CLASS = "rt-table-scroll"

/** Hosts that count as "this site" — links to these stay in the same tab. */
const INTERNAL_HOSTS = ["mazda.co.il", "www.mazda.co.il"]

const isExternalHref = (href: string) => {
  if (!/^(https?:)?\/\//i.test(href)) return false

  try {
    const { hostname } = new URL(
      href.startsWith("//") ? `https:${href}` : href
    )
    return !INTERNAL_HOSTS.includes(hostname.toLowerCase())
  } catch {
    return false
  }
}

/** Adds `loading="lazy"` to images that don't already declare a loading mode. */
const withLazyImages = (html: string) =>
  html.replace(/<img\b([^>]*)>/gi, (tag, attrs: string) =>
    /\bloading\s*=/i.test(attrs)
      ? tag
      : `<img${attrs} loading="lazy" decoding="async">`
  )

/**
 * Wraps each table in a scroll container.
 *
 * Non-greedy match to the first `</table>` — the CMS rich text editor cannot
 * produce nested tables, so there is no inner table to swallow.
 */
const withScrollableTables = (html: string) =>
  html.replace(
    /<table\b[\s\S]*?<\/table>/gi,
    (table) => `<div class="${TABLE_SCROLL_CLASS}">${table}</div>`
  )

/**
 * Points external links at a new tab. `noopener` is the requirement; `noreferrer`
 * rides along because a target-blank link has no reason to leak the referrer.
 * Links that already declare a target are left as the editor authored them.
 */
const withExternalLinkSafety = (html: string) =>
  html.replace(/<a\b([^>]*)>/gi, (tag, attrs: string) => {
    if (/\btarget\s*=/i.test(attrs)) return tag

    const href = attrs.match(/\bhref\s*=\s*["']([^"']*)["']/i)?.[1]
    if (!href || !isExternalHref(href)) return tag

    const rel = attrs.match(/\brel\s*=\s*["']([^"']*)["']/i)?.[1]
    if (rel) {
      const merged = Array.from(
        new Set([...rel.split(/\s+/), "noopener", "noreferrer"])
      )
        .filter(Boolean)
        .join(" ")
      return `<a${attrs.replace(
        /\brel\s*=\s*["'][^"']*["']/i,
        `rel="${merged}"`
      )} target="_blank">`
    }

    return `<a${attrs} target="_blank" rel="noopener noreferrer">`
  })

export const prepareContentPageHtml = (html: string) =>
  withExternalLinkSafety(withScrollableTables(withLazyImages(html)))

const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
}

const toPlainText = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(
      /&[a-z]+;|&#39;/gi,
      (entity) => HTML_ENTITIES[entity.toLowerCase()] ?? entity
    )
    .replace(/\s+/g, " ")
    .trim()

/**
 * Reads the page heading out of the rich text.
 *
 * The CMS has no title field — the heading is authored inside the body — so this
 * is the only source for a per-page browser tab title. Prefers the `h1`; falls
 * back to the first heading of any level for content that starts lower down.
 * Returns undefined when the body has no heading at all.
 */
export const extractContentPageHeading = (html: string) => {
  const headings = [...html.matchAll(/<(h[1-4])\b[^>]*>([\s\S]*?)<\/\1>/gi)]
  if (!headings.length) return undefined

  const chosen =
    headings.find(([, tag]) => tag.toLowerCase() === "h1") ?? headings[0]

  return toPlainText(chosen[2]) || undefined
}
